import { useEffect, useRef } from 'react';
import { turnChime } from './sound.js';
import { useStore } from './store.js';

/**
 * Tells you it is your go when you are not watching.
 *
 * Three separate nudges, because no single one covers the ways people wait:
 * a chime for anyone listening, the tab title for anyone who has switched
 * away, and a wake lock so a phone left on the table does not sleep through
 * three AI turns.
 */

interface WakeLockSentinel {
  released: boolean;
  release(): Promise<void>;
  addEventListener(type: 'release', listener: () => void): void;
}

type WakeLockNavigator = Navigator & {
  wakeLock?: { request(type: 'screen'): Promise<WakeLockSentinel> };
};

export function useTurnAlerts(): { isMyTurn: boolean } {
  const view = useStore((s) => s.game);
  const soundOn = useStore((s) => s.sound);
  const screen = useStore((s) => s.screen);

  const me = view?.yourPlayerId ?? null;
  const finished = view?.phase === 'finished';
  const isMyTurn =
    !!view &&
    !finished &&
    (view.phase === 'choosingSuit' ? view.awaitingSuitFrom === me : view.currentPlayerId === me);

  // --- The chime ----------------------------------------------------------
  // Keyed on the turn number as well as whose it is, so a reconnect that
  // re-sends the same view does not chime a second time.
  const lastChimed = useRef<number | null>(null);
  useEffect(() => {
    if (!isMyTurn || !view) {
      if (!isMyTurn) lastChimed.current = null;
      return;
    }
    if (lastChimed.current === view.turnNumber) return;
    lastChimed.current = view.turnNumber;
    if (soundOn) turnChime();
  }, [isMyTurn, view, soundOn]);

  // The tab title is deliberately not set here. App owns it — it is the only
  // place that knows about lobby and site pages too, and two writers would
  // race whenever a page changed mid-turn.

  // --- The wake lock ------------------------------------------------------
  // Held for the whole time you are at a table, not just your turn: the point
  // is to be able to watch the game, and the browser drops it on tab-hide
  // anyway, so it has to be re-taken when you come back.
  const seated = screen === 'game';
  useEffect(() => {
    if (!seated) return;
    const api = (navigator as WakeLockNavigator).wakeLock;
    if (!api) return;

    let sentinel: WakeLockSentinel | null = null;
    let cancelled = false;

    const acquire = async () => {
      if (cancelled || document.hidden || sentinel) return;
      try {
        sentinel = await api.request('screen');
        sentinel.addEventListener('release', () => {
          sentinel = null;
        });
      } catch {
        // Denied, unsupported, or the tab lost focus mid-request. The game is
        // perfectly playable without it.
      }
    };

    const onVisibility = () => {
      if (document.hidden) return;
      void acquire();
    };

    void acquire();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      void sentinel?.release().catch(() => {});
      sentinel = null;
    };
  }, [seated]);

  return { isMyTurn };
}
